import React from 'react';

/**
 * ErrorBoundary — catches uncaught render/lifecycle errors anywhere below
 * so the app never bails out to a blank/loader screen without explanation.
 *
 * When something throws we render a compact Arabic-first fallback with:
 *   • a clear "حدث خطأ" message
 *   • the raw error message + first frame of the stack (helpful for support)
 *   • a "إعادة المحاولة" reset button
 *   • a "إعادة تحميل الصفحة" full-reload button
 * Also logs to console so it appears in DevTools if the merchant shares logs.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Persist for the fallback + emit to devtools console.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);

    // iter-232: Auto-recover from Google Translate DOM crashes on mobile.
    // Chrome mobile auto-translates Arabic RTL pages, injecting <font> nodes
    // React doesn't know about. When React tries to unmount (Sonner toasts,
    // wizard step transitions, dropdown close), it throws:
    //   "Failed to execute 'removeChild' on 'Node':
    //    The node to be removed is not a child of this Node."
    // These are self-healing — clearing the error state on the next tick
    // triggers a fresh render that skips the stale reference. We ONLY
    // auto-recover for this specific class of DOM-manipulation error;
    // real bugs still surface the red fallback UI.
    const msg = String(error?.message || '');
    const isTranslatorDomError =
      msg.includes("removeChild") ||
      msg.includes("insertBefore") ||
      msg.includes("appendChild") ||
      msg.includes("is not a child of this");
    if (isTranslatorDomError) {
      // Give the browser one tick to reconcile, then quietly try again.
      setTimeout(() => this.setState({ error: null, info: null }), 0);
      return;
    }

    this.setState({ info });
  }

  handleReset = () => {
    this.setState({ error: null, info: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    const msg = String(this.state.error?.message || this.state.error);
    const stackFirstLine = (this.state.info?.componentStack || '').split('\n').slice(0, 4).join('\n');
    return (
      <div
        dir="rtl"
        data-testid="app-error-boundary"
        className="min-h-screen flex items-center justify-center bg-[#0B0F19] px-4 py-8"
      >
        <div className="max-w-lg w-full rounded-2xl border border-red-500/30 bg-[#131B2C] shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-red-500 to-rose-600 p-5 text-white text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-white/20 flex items-center justify-center mb-3 text-3xl">
              !
            </div>
            <h2 className="text-xl font-bold mb-1">حدث خطأ غير متوقع</h2>
            <p className="text-sm text-white/85">اضغط "إعادة المحاولة" — لو استمرت المشكلة، أعد تحميل الصفحة.</p>
          </div>
          <div className="p-5 space-y-3">
            <div className="rounded-xl bg-red-500/10 border border-red-500/30 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-red-400 mb-1">تفاصيل الخطأ</p>
              <p className="text-sm text-red-200 font-mono break-words" dir="ltr">{msg}</p>
              {stackFirstLine && (
                <pre className="mt-2 text-[10px] text-red-300/70 whitespace-pre-wrap font-mono" dir="ltr">
                  {stackFirstLine}
                </pre>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <button
                type="button"
                onClick={this.handleReset}
                data-testid="error-boundary-reset-btn"
                className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#38BDF8] hover:bg-[#0EA5E9]"
              >
                إعادة المحاولة
              </button>
              <button
                type="button"
                onClick={this.handleReload}
                data-testid="error-boundary-reload-btn"
                className="px-4 py-2.5 rounded-xl text-sm font-semibold border border-white/10 text-white bg-white/5 hover:bg-white/10"
              >
                إعادة تحميل الصفحة
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
